import React, { useCallback, useEffect, useState } from "react";
import "./tenantonboard.css";
import { FORM_CONTROLFIELD_TYPES } from "../../../constants/constants";
import { OnboardingForms } from "../../../typings/onboarding";
import {
  DocumentEntityTypesEnum,
  DocumentService,
  DocumentTypesEnum,
  LeaseApplicationEmployerVM,
  LeaseApplicationRentIncomeVM,
  RentPayTypesEnum,
} from "@propertelligent/client-api";
import CustomInputs from "../../common/Inputs";
import RadioButton from "../../common/RadioButton";
import DragAndDropUploader from "../../common/CustomUploader/DragAndDropUploader";
import DisplayNamewithLeftBorder from "../../common/DisplayNamewithLeftBorder";
import DateHelper from "../../../utils/DateHelper";
import { useError } from "../../../utils/context/ErrorContext";
import { GetImages } from "../../../utils/GetImages";
import { TextCss } from "../../GlobalCss/TextCss";

const StepTwo: React.FC<any> = ({
  formData,
  setFormData,
  docsArr,
  setDocsArr,
  leaseApplicationid,
  currentStepIndex,
  RentIncomeCheck,
  tenantId
}: {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<OnboardingForms>>;
  docsArr: any;
  setDocsArr: any;
  leaseApplicationid: Number;
  currentStepIndex: any;
  RentIncomeCheck: any;
  tenantId:any
}) => {
  const { errors, setErrors, errorMessages, setErrorMessages, clearErrors } = useError();

  // ✅ Independent UI state for isCurrentlyEmployed
  const [isCurrentlyEmployed, setIsCurrentlyEmployed] = useState(true);

  const getDocuments = async (documentTypeId) => {
    try {
      const res = await DocumentService.getApiDocumentList(
        Number(tenantId), //tenantid
        DocumentEntityTypesEnum.Tenant,
        documentTypeId
      );

      let documentArray = res?.success || [];

      if (documentArray.length > 0) {
        await Promise.all(
          documentArray.map(async (item) => {
            if (["png", "jpg", "jpeg"].includes(item?.fileName?.split(".")[1]?.toLowerCase())) {
              try {
                const base64Res = await DocumentService.getApiDocumentBase641(item.id);
                item["docData"] = "data:image/png;base64," + base64Res.success;
              } catch (error) {
                console.error("Error fetching base64 data:", error);
              }
            }
          })
        );
      }

      return documentArray;
    } catch (error) {
      console.error("Error fetching documents:", error);
      return [];
    }
  };

  const setdata = useCallback(async () => {
    if (!RentIncomeCheck) return;

    try {
      let newArr = [];

      if (formData?.rentPayTypeId === RentPayTypesEnum.Income) {
        newArr = await getDocuments(DocumentTypesEnum.IncomeVerification);
      } else if (formData?.rentPayTypeId === RentPayTypesEnum.Assisted) {
        newArr = await getDocuments(DocumentTypesEnum.AssistanceVerification);
      } else {
        const [array, array1] = await Promise.all([
          getDocuments(DocumentTypesEnum.IncomeVerification),
          getDocuments(DocumentTypesEnum.AssistanceVerification),
        ]);

        newArr = [...array, ...array1];
      }

      setDocsArr((prev) => [...newArr, ...prev.filter((d) => !d?.id)]);

      setFormData((prevFormData) => ({
        ...prevFormData,
        RentIncomeCheck:
          prevFormData?.RentIncomeCheck ||
          ({
            rentPayTypeId: RentPayTypesEnum.Both,
            employerDetails: {
              firstName: "",
              lastName: "",
              email: "",
              phoneNumber: "",
              name: "",
              startDate: "",
              endDate: null,
            } as LeaseApplicationEmployerVM,
            assistedDetails: {
              firstName: "",
              lastName: "",
              email: "",
              phoneNumber: "",
              name: "",
              startDate: "",
              endDate: "",
            } as LeaseApplicationEmployerVM,
            employmentIncome: undefined,
            assistedAmount: undefined,
          } as LeaseApplicationRentIncomeVM),
      }));
    } catch (error) {}
  }, [RentIncomeCheck, formData?.rentPayTypeId]);

  useEffect(() => {
    clearErrors();
    setdata();
  }, [RentIncomeCheck, formData?.rentPayTypeId]);

  // ------------------------ HANDLE FORM CHANGES ---------------------------- //

  const handleChange = useCallback(
    (key: string, value: any, subKey?: string) => {
      setFormData((prevData: OnboardingForms) => {
        let updatedRentIncomeCheck = { ...prevData.RentIncomeCheck };
        updatedRentIncomeCheck[key] = subKey
          ? { ...updatedRentIncomeCheck?.[key], [subKey]: value }
          : value;

        const rentPayTypeId = updatedRentIncomeCheck.rentPayTypeId;

        switch (rentPayTypeId) {
          case RentPayTypesEnum.Income:
            updatedRentIncomeCheck = {
              rentPayTypeId: RentPayTypesEnum.Income,
              employerDetails: updatedRentIncomeCheck?.employerDetails || {
                name: null,
                startDate: null,
                firstName: null,
                lastName: null,
                email: null,
                phoneNumber: null,
                endDate: null,
              },
              employmentIncome: updatedRentIncomeCheck?.employmentIncome || null,
              assistedDetails: updatedRentIncomeCheck?.assistedDetails,
              assistedAmount: updatedRentIncomeCheck?.assistedAmount,
            };
            break;

          case RentPayTypesEnum.Assisted:
            updatedRentIncomeCheck = {
              rentPayTypeId: RentPayTypesEnum.Assisted,
              assistedDetails: updatedRentIncomeCheck?.assistedDetails || {
                name: null,
                startDate: null,
                endDate: null,
                firstName: null,
                lastName: null,
                email: null,
                phoneNumber: null,
              },
              assistedAmount: updatedRentIncomeCheck?.assistedAmount || null,
              employerDetails: updatedRentIncomeCheck?.employerDetails,
              employmentIncome: updatedRentIncomeCheck?.employmentIncome,
            };
            break;

          case RentPayTypesEnum.Both:
            updatedRentIncomeCheck = {
              rentPayTypeId: RentPayTypesEnum.Both,
              employerDetails: updatedRentIncomeCheck?.employerDetails || {
                name: null,
                startDate: null,
                firstName: null,
                lastName: null,
                email: null,
                phoneNumber: null,
                endDate: null,
              },
              assistedDetails: updatedRentIncomeCheck?.assistedDetails || {
                name: null,
                startDate: null,
                endDate: null,
                firstName: null,
                lastName: null,
                email: null,
                phoneNumber: null,
              },
              employmentIncome: updatedRentIncomeCheck?.employmentIncome || null,
              assistedAmount: updatedRentIncomeCheck?.assistedAmount || null,
            };
            break;

          default:
            break;
        }

        return {
          ...prevData,
          RentIncomeCheck: updatedRentIncomeCheck,
        };
      });

      const errorKey = subKey ? `${key}.${subKey}` : key;
      setErrors((prevErrors) => ({ ...prevErrors, [errorKey]: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, [errorKey]: "" }));
    },
    [setFormData, formData?.rentPayTypeId]
  );

  // ------------------------ FILE UPLOAD ---------------------------- //

  const handleUploadServiceRequestDocument = async (selectedFile, DocumentTypeId) => {
    if (!selectedFile) return;

    const validTypes = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];
    if (!validTypes.includes(selectedFile.type)) {
      setErrors((prev) => ({ ...prev, FileType: true, FileSize: false }));
      setErrorMessages((prev) => ({
        ...prev,
        FileType: "Invalid file type. Only PDF, PNG, JPG are allowed.",
        FileSize: "",
      }));
      return;
    }

    const maxSize = 1 * 1024 * 1024;
    if (selectedFile.size > maxSize) {
      setErrors((prev) => ({ ...prev, FileType: false, FileSize: true }));
      setErrorMessages((prev) => ({
        ...prev,
        FileType: "",
        FileSize: "File size should not exceed 1MB",
      }));
      return;
    }

    setErrors((prev) => ({ ...prev, FileType: false, FileSize: false }));
    setErrorMessages((prev) => ({ ...prev, FileType: "", FileSize: "" }));

    const newDoc = {
      EntityTypeId: DocumentEntityTypesEnum.Tenant,
      EntityId: tenantId, //tenantid
      documentTypeId: DocumentTypeId, //
      IsMain: false,
      fileName: selectedFile?.name,
      Document: selectedFile,
      _rawFile: selectedFile,
    };

    setDocsArr((prev) => [...prev, newDoc]);
  };

  // ------------------------ RENDERING CONFIG ---------------------------- //

  const Radiodata = [
    {
      id: RentPayTypesEnum.Income,
      label: "Income",
      checked: formData?.rentPayTypeId == RentPayTypesEnum.Income,
    },
    {
      id: RentPayTypesEnum.Assisted,
      label: "Assisted",
      checked: formData?.rentPayTypeId == RentPayTypesEnum.Assisted,
    },
    {
      id: RentPayTypesEnum.Both,
      label: "Both",
      checked: formData?.rentPayTypeId === RentPayTypesEnum.Both,
    },
  ];

  const CurrentEmployee = [
    {
      id: 1,
      name: "name",
      labelText: "Employer Name",
      placeholder: "Enter Employer Name",
      myClassName: "containerTextField",
      value: formData?.employerDetails?.name,
      errorMessages: errorMessages?.["employerDetails.name"],
      isError: errors["employerDetails.name"],
      type: FORM_CONTROLFIELD_TYPES.TEXT,
      width: "100%",
      maxLength: 40,
    },
    {
      id: 2,
      name: "employmentIncome",
      value: formData?.employmentIncome,
      labelText: "Gross Monthly Income",
      placeholder: "Enter Gross Monthly Income",
      myClassName: undefined,
      isError: errors[`employmentIncome`],
      errorMessages: (errorMessages as any)?.employmentIncome,
      type: FORM_CONTROLFIELD_TYPES.NUMBER,
      width: "100%",
      currencySymbol: "$",
      isCurrency: true,
    },
    {
      id: 3,
      name: "startDate",
      value: DateHelper.convertUTCtoDateTime2(formData?.employerDetails?.startDate),
      placeholder: "Select Start Date",
      labelText: "Start Date",
      label_img:GetImages.calendarIcon,
      isError: errors["employerDetails.startDate"],
      errorMessages: errorMessages?.["employerDetails.startDate"],
      type: FORM_CONTROLFIELD_TYPES.DATE,
      width: "100%",
    }
  ];
  useEffect(() => {
  const endDate = formData?.employerDetails?.endDate;

  if (endDate === null ||  endDate === undefined) {
    setIsCurrentlyEmployed(true);   // checked
  } else {
    setIsCurrentlyEmployed(false);  // unchecked
  }
}, [formData?.employerDetails?.endDate]);

  // ------------------------ JSX ---------------------------- //


useEffect(() => {
  const endDate = formData?.RentIncomeCheck?.employerDetails?.endDate;

  if (endDate === "") {
    setFormData((prev) => ({
      ...prev,
      RentIncomeCheck: {
        ...prev.RentIncomeCheck,
        employerDetails: {
          ...prev.RentIncomeCheck?.employerDetails,
          endDate: null, // ✅ normalize ONLY here
        },
      },
    }));
  }
}, []);

  return (
    <>
     <div className="d-flex flex-column gap-2">
      <p className={TextCss.black.sub_heading_600_14}>How you will be Paying Rent? <span className="red">*</span></p>
      <div className="flexRow gap-5 rent-pay-type-container">
        {Radiodata?.map((item) => (
          <div key={item?.id}>
            <RadioButton
              id={`${item.id}`}
              label={item.label}
              value={formData?.rentPayTypeId || RentPayTypesEnum.Income}
              name={"rentPayTypeId"}
              checked={item.checked}
              onChange={() => {
                clearErrors();
                handleChange("rentPayTypeId", item.id);
              }}
            />
          </div>
        ))}
      </div>
      </div>

      {/* INCOME SECTION */}
      {(formData?.rentPayTypeId === RentPayTypesEnum.Income ||
        formData?.rentPayTypeId === RentPayTypesEnum.Both) && (
        <div className="d-flex flex-column gap-3 py-3">
          <DisplayNamewithLeftBorder HeaderName={"Current Employer details"} />

          <div className="rentIncomeCheck py-1 ">
            {/* Existing employer fields */}
            {CurrentEmployee?.map((item) => (
              <div key={item?.id} >
                <CustomInputs
                  name={item.name}
                  type={item.type}
                  value={item.value}
                  labelText={item.labelText}
                  // label_img={item.label_img}
                  isFormControl={true}
                  onChange={(e) =>
                    item.name == "employmentIncome"
                      ? handleChange("employmentIncome", e.target.value)
                      : handleChange("employerDetails", e.target.value, item.name)
                  }
                  placeholder={item.placeholder}
                  myClassName={item.myClassName}
                  isError={item.isError}
                  errorMessages={item.errorMessages}
                  required={true}
                  width={item.width}
                  currencySymbol={item.currencySymbol}
                  currencyHeight={"48px"}
                  isCurrency={(item as any).isCurrency || false}
                />
              </div>
            ))}
            <div className={`${isCurrentlyEmployed ? "mt-4" : ""} rentpaychecktype `}>
            {!isCurrentlyEmployed && (
              <CustomInputs
                name="endDate"
                type={FORM_CONTROLFIELD_TYPES.DATE}
                value={DateHelper.convertUTCtoDateTime2(
                  formData?.employerDetails?.endDate
                )}
                labelText="End Date"
                placeholder="Select end date"
                isFormControl={true}
                onChange={(e) =>
                  handleChange("employerDetails", e.target.value, "endDate")
                }
                myClassName="containerTextField"
                isError={errors["employerDetails.endDate"]}
                errorMessages={errorMessages.endDate}
                width="100%"
              />
            )}
            <RadioButton
              id="isCurrentlyEmployed"
              type="checkbox"
              label="Currently Employed"
              checked={isCurrentlyEmployed}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsCurrentlyEmployed(checked);

                if (checked) {
                  handleChange("employerDetails", null, "endDate");
                }
              } } value={undefined} name={""}          
            /></div>
         
          </div>
          {/* CONTACT DETAILS */}
          <div>
            <DisplayNamewithLeftBorder HeaderName={"CONTACT DETAILS"} />
            <div className="rentIncomeCheck py-3">
              {[
                {
                  id: 1,
                  name: "firstName",
                  labelText: "First Name",
                  type: FORM_CONTROLFIELD_TYPES.TEXT,
                  value: formData?.employerDetails?.firstName,
                  placeholder: "Enter First Name",
                  myClassName: "containerTextField",
                  isError: errors?.["employerDetails.firstName"],
                  errorMessages: errorMessages?.["employerDetails.firstName"],
                  width: "100%",
                  maxLength: 20,
                },
                {
                  id: 2,
                  name: "lastName",
                  labelText: "Last Name",
                  type: FORM_CONTROLFIELD_TYPES.TEXT,
                  value: formData?.employerDetails?.lastName,
                  placeholder: "Enter Last Name",
                  myClassName: "containerTextField",
                  isError: errors?.["employerDetails.lastName"],
                  errorMessages: errorMessages?.["employerDetails.lastName"],
                  width: "100%",
                  maxLength: 20,
                },
                {
                  id: 3,
                  name: "email",
                  labelText: "Email",
                  type: FORM_CONTROLFIELD_TYPES.EMAIL,
                  value: formData?.employerDetails?.email,
                  placeholder: "Enter Email",
                  myClassName: "containerTextField",
                  isError: errors?.["employerDetails.email"],
                  errorMessages: errorMessages?.["employerDetails.email"],
                  width: "100%",
                  maxLength: 50,
                },
                {
                  id: 4,
                  name: "phoneNumber",
                  labelText: "Contact No",
                  type: FORM_CONTROLFIELD_TYPES.MOBILE,
                  value: formData?.employerDetails?.phoneNumber,
                  placeholder: "Enter Contact No",
                  myClassName: "containerTextField",
                  isError: errors?.["employerDetails.phoneNumber"],
                  errorMessages: errorMessages?.["employerDetails.phoneNumber"],
                  maxLength: 10,
                  width: "100%",
                },
              ].map((item) => (
                <div key={item?.id}>
                  <CustomInputs
                    name={item.name}
                    type={item.type}
                    value={item.value}
                    labelText={item.labelText}
                    isFormControl={true}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      let sanitizedValue = value;

                      if (name === "firstName" || name === "lastName") {
                        sanitizedValue = value.replace(/[^a-zA-Z ]/g, "");
                      }

                      handleChange("employerDetails", sanitizedValue, item.name);
                    }}
                    placeholder={item.placeholder}
                    myClassName={item.myClassName}
                    isError={item.isError}
                    errorMessages={item.errorMessages}
                    maxLength={item.maxLength}
                    required={true}
                    width={item.width}
                    sectionName="employerDetails"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* DOCUMENT UPLOAD */}
          <div className="pb-2">
            <DragAndDropUploader
              handleChange={(filename) =>{clearErrors();
                handleUploadServiceRequestDocument(
                  filename,
                  DocumentTypesEnum.IncomeVerification
                )}
              }
              docsArr={docsArr.filter(
                (item) =>
                  item.documentTypeId == DocumentTypesEnum.IncomeVerification
              )}
              setDocsArr={setDocsArr}
              name="Income Verification Document"
            />
            {errors.incomeVerDoc && (
  <p className="errorMessageStyles pt-2">{errorMessages.incomeVerDoc}</p>
)}
          </div>
        </div>
      )}

      {/* ASSISTED SECTION */}
      {(formData?.rentPayTypeId === RentPayTypesEnum.Assisted ||
        formData?.rentPayTypeId === RentPayTypesEnum.Both) && (
        <div className="d-flex flex-column gap-3">
          <DisplayNamewithLeftBorder HeaderName={"ASSISTED DETAILS"} />

          <div className="rentIncomeCheck py-1">
            {[
              {
                id: 1,
                name: "name",
                labelText: "Authority Name",
                type: FORM_CONTROLFIELD_TYPES.TEXT,
                value: formData?.assistedDetails?.name,
                placeholder: "Enter last name",
                myClassName: "containerTextField",
                isError: errors["assistedDetails.name"],
                errorMessages: errorMessages?.name,
                width: "100%",
              },
              {
                id: 2,
                name: "assistedAmount",
                labelText: "Authority Amount",
                type: FORM_CONTROLFIELD_TYPES.NUMBER,
                value: formData?.assistedAmount,
                placeholder: "Enter Amount",
                myClassName: "containerTextField",
                isError: errors[`assistedAmount`],
                errorMessages: (errorMessages as any)?.assistedAmount,
                width: "100%",
                currencySymbol: "$",
                isCurrency:true
              },
              {
                id: 3,
                name: "startDate",
                value: DateHelper.convertUTCtoDateTime2(
                  formData?.assistedDetails?.startDate
                ),
                 isError: errors[`assistedDetails.endDate`],
                errorMessages: errorMessages.endDate,
                labelText: "Start Date",
                type: FORM_CONTROLFIELD_TYPES.DATE,
                width: "100%",
              },
              {
                id: 4,
                name: "endDate",
                value: DateHelper.convertUTCtoDateTime2(
                  formData?.assistedDetails?.endDate
                ),
                isError: errors[`assistedDetails.endDate`],
                errorMessages: errorMessages.startDate,
                labelText: "End Date",
                type: FORM_CONTROLFIELD_TYPES.DATE,
                width: "100%",
              },
            ].map((item) => (
              <div key={item?.id}>
                <CustomInputs
                  name={item.name}
                  type={item.type}
                  value={item.value}
                  labelText={item.labelText}
                  isFormControl={true}
                  onChange={(e) =>
                    item.name == "assistedAmount"
                      ? handleChange("assistedAmount", e.target.value)
                      : handleChange("assistedDetails", e.target.value, item.name)
                  }
                  placeholder={item.placeholder}
                  myClassName={item.myClassName}
                  isError={item.isError}
                  errorMessages={item.errorMessages}
                  required={true}
                  width={item.width}
                  isCurrency={(item as any).isCurrency || false}
                />
              </div>
            ))}
          </div>

          {/* SOCIAL WORKER DETAILS */}
          <div>
            <DisplayNamewithLeftBorder HeaderName={"YOUR SOCIAL WORKER DETAILS"} />

            <div className="rentIncomeCheck py-3">
              {[
                {
                  id: 1,
                  name: "firstName",
                  labelText: "First Name",
                  type: FORM_CONTROLFIELD_TYPES.TEXT,
                  value: formData?.assistedDetails?.firstName,
                  placeholder: "Enter First Name",
                  myClassName: "containerTextField",
                  isError: errors?.["assistedDetails.firstName"],
                  errorMessages: errorMessages?.["assistedDetails.firstName"],
                  width: "100%",
                  maxLength: 20,
                },
                {
                  id: 2,
                  name: "lastName",
                  labelText: "Last Name",
                  type: FORM_CONTROLFIELD_TYPES.TEXT,
                  value: formData?.assistedDetails?.lastName,
                  placeholder: "Enter last Name",
                  myClassName: "containerTextField",
                  isError: errors?.["assistedDetails.lastName"],
                  errorMessages: errorMessages?.["assistedDetails.lastName"],
                  width: "100%",
                  maxLength: 20,
                },
                {
                  id: 3,
                  name: "email",
                  labelText: "Email",
                  type: FORM_CONTROLFIELD_TYPES.EMAIL,
                  value: formData?.assistedDetails?.email,
                  placeholder: "Enter Email",
                  myClassName: "containerTextField",
                  isError: errors?.["assistedDetails.email"],
                  errorMessages: errorMessages?.["assistedDetails.email"],
                  width: "100%",
                  maxLength: 50,
                },
                {
                  id: 4,
                  name: "phoneNumber",
                  labelText: "Contact No",
                  type: FORM_CONTROLFIELD_TYPES.MOBILE,
                  value: formData?.assistedDetails?.phoneNumber,
                  placeholder: "Enter Contact No",
                  myClassName: "containerTextField",
                  isError: errors?.["assistedDetails.phoneNumber"],
                  errorMessages: errorMessages?.["assistedDetails.phoneNumber"],
                  maxLength: 10,
                  width: "100%",
                },
              ].map((item) => (
                <div key={item?.id}>
                  <CustomInputs
                    name={item.name}
                    type={item.type}
                    value={item.value}
                    labelText={item.labelText}
                    isFormControl={true}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      let sanitizedValue = value;

                      if (name === "firstName" || name === "lastName") {
                        sanitizedValue = value.replace(/[^a-zA-Z ]/g, "");
                      }

                      handleChange("assistedDetails", sanitizedValue, item.name);
                    }}
                    placeholder={item.placeholder}
                    myClassName={item.myClassName}
                    isError={item.isError}
                    errorMessages={item.errorMessages}
                    maxLength={item.maxLength}
                    required={true}
                    width={item.width}
                    sectionName="assistedDetails"
                    isCurrency={(item as any).isCurrency || false}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* DOCUMENT UPLOAD */}
          <div className="pb-4">
            <DragAndDropUploader
              handleChange={(filename) =>{
                clearErrors();
                handleUploadServiceRequestDocument(
                  filename,
                  DocumentTypesEnum.AssistanceVerification
                )}
              }
              docsArr={docsArr.filter(
                (item) =>
                  item.documentTypeId == DocumentTypesEnum.AssistanceVerification
              )}
              setDocsArr={setDocsArr}
              name="Assistance Verification Document"
            />
              {errors.assistVerDoc && (
             <p className="errorMessageStyles pt-2">{errorMessages.assistVerDoc}</p>
              )}
          </div>
        </div>
      )}
    </>
  );
};

export default StepTwo;


